import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from './Button';
import { apiError } from '../../lib/utils';

interface Toast { id: number; text: string }
interface Fault { id: number; text: string }
interface Ask { title: string; explain: string }
interface RunOptions { confirm?: string | false }

export interface ProcessRun {
  id: string;
  title: string;
  status: 'running' | 'done' | 'failed' | 'cancelled';
  lines: string[];
  startedAt: number;
  error?: string;
  group?: string;
}

interface GroupItem<T> {
  title: string;
  work: (log: (line: string) => void, signal: AbortSignal) => Promise<T>;
}

export interface OpenProcess {
  id: string;
  log: (line: string) => void;
  finish: (status: 'done' | 'failed' | 'cancelled', error?: string) => void;
  bindCancel: (cancel: () => void) => void;
}

interface NoticeApi {
  busy: boolean;
  processes: ProcessRun[];
  success: (text: string) => void;
  failure: (text: string) => void;
  confirm: (title: string, explain: string) => Promise<boolean>;
  openProcess: (title: string, group?: string) => OpenProcess;
  cancelProcess: (id: string) => void;
  dismissProcess: (id: string) => void;
  run: <T>(label: string, work: (log: (line: string) => void, signal: AbortSignal) => Promise<T>, successText: string | ((value: T) => string), options?: RunOptions) => Promise<T | undefined>;
  runGroup: <T>(label: string, items: GroupItem<T>[], options?: RunOptions) => Promise<(T | undefined)[] | undefined>;
}

const NoticeContext = createContext<NoticeApi | null>(null);

export function useNotice(): NoticeApi {
  const value = useContext(NoticeContext);
  if (!value) throw new Error('Notice is not mounted');
  return value;
}

export function useNoticeBusy(): boolean {
  return useContext(NoticeContext)?.busy ?? false;
}

function processStatus(status: ProcessRun['status']): string {
  if (status === 'running') return 'Running';
  if (status === 'done') return 'Finished';
  if (status === 'failed') return 'Failed';
  return 'Cancelled';
}

function GroupRunDialog({ label, items, selectedId, onSelect, onCancel, onBackground }: {
  label: string;
  items: ProcessRun[];
  selectedId: string;
  onSelect: (id: string) => void;
  onCancel: (id: string) => void;
  onBackground: () => void;
}) {
  const logRef = useRef<HTMLDivElement>(null);
  const selected = items.find((item) => item.id === selectedId) || items.find((item) => item.status === 'running') || items[0];
  useEffect(() => {
    logRef.current?.scrollTo(0, logRef.current.scrollHeight);
  }, [selected?.id, selected?.lines.length]);
  if (!selected) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-end bg-gray-900/40 md:items-center md:justify-center md:p-4" onMouseDown={(event) => { event.stopPropagation(); if (event.target === event.currentTarget) onBackground(); }}>
      <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white px-4 py-5 shadow-2xl dark:border-gray-700 dark:bg-gray-900 md:rounded-2xl md:px-6" role="status" aria-live="polite" aria-busy="true">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 flex-1 text-base font-medium">{label}</p>
          <button type="button" aria-label="Close" className="shrink-0 rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" onClick={onBackground}><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div className="max-h-80 overflow-auto rounded-xl border border-gray-200 dark:border-gray-700">
            {[...items].sort((a, b) => Number(a.id) - Number(b.id)).map((item) => (
              <button key={item.id} type="button" className={`flex w-full items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0 dark:border-gray-800 ${item.id === selected.id ? 'bg-gray-100 dark:bg-gray-800' : ''}`} onClick={() => onSelect(item.id)}>
                <span className="truncate">{item.title}</span>
                <span className={`shrink-0 ${item.status === 'running' ? 'text-blue-600' : item.status === 'failed' ? 'text-red-600' : 'text-gray-500'}`}>{processStatus(item.status)}</span>
              </button>
            ))}
          </div>
          <div>
            <div className="flex items-center gap-3">
              {selected.status === 'running' ? <span className="bo-spinner" aria-hidden="true" /> : null}
              <p className="truncate text-sm font-medium">{selected.title}</p>
            </div>
            <div ref={logRef} className="max-h-64 overflow-auto">
              <div className="bo-terminal">
                {(selected.lines.length ? selected.lines : ['']).map((line, index) => <div key={index}>{line}</div>)}
              </div>
            </div>
            {selected.error ? <p className="mt-2 text-sm text-red-600">{selected.error}</p> : null}
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-2 md:flex-row md:justify-end">
          {selected.status === 'running' ? <Button variant="outline" onClick={() => onCancel(selected.id)}>Cancel</Button> : null}
          <Button onClick={onBackground}>Run in background</Button>
        </div>
      </div>
    </div>
  );
}

function SuccessToast({ text, onDone }: { text: string; onDone: () => void }) {
  const [fade, setFade] = useState(false);
  useEffect(() => {
    const fadeAt = window.setTimeout(() => setFade(true), 3000);
    const doneAt = window.setTimeout(onDone, 5000);
    return () => {
      window.clearTimeout(fadeAt);
      window.clearTimeout(doneAt);
    };
  }, [onDone]);
  return (
    <div
      className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 shadow-lg dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100"
      style={{ opacity: fade ? 0 : 1, transition: 'opacity 2s linear' }}
    >
      {text}
    </div>
  );
}

export function NoticeProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [faults, setFaults] = useState<Fault[]>([]);
  const [processes, setProcesses] = useState<ProcessRun[]>([]);
  const [foregroundId, setForegroundId] = useState('');
  const [foregroundGroup, setForegroundGroup] = useState('');
  const [groupLabel, setGroupLabel] = useState('');
  const groups = useRef(new Map<string, string>());
  const [ask, setAsk] = useState<Ask | null>(null);
  const askRef = useRef<((ok: boolean) => void) | null>(null);
  const cancels = useRef(new Map<string, () => void>());
  const seq = useRef(0);
  const success = useCallback((text: string) => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, text }]);
  }, []);
  const failure = useCallback((text: string) => {
    setFaults((current) => current.some((item) => item.text === text) ? current : [...current, { id: Date.now() + Math.random(), text }]);
  }, []);
  const confirm = useCallback((title: string, explain: string) => new Promise<boolean>((resolve) => {
    askRef.current = resolve;
    setAsk({ title, explain });
  }), []);
  const answer = useCallback((ok: boolean) => {
    const resolve = askRef.current;
    askRef.current = null;
    setAsk(null);
    resolve?.(ok);
  }, []);
  const openProcess = useCallback((title: string, group = ''): OpenProcess => {
    const id = String(++seq.current);
    if (group) groups.current.set(id, group);
    setProcesses((current) => [{ id, title, status: 'running', lines: [title], startedAt: Date.now(), group: group || undefined }, ...current]);
    const log = (line: string) => {
      const text = line.trim();
      if (!text) return;
      setProcesses((current) => current.map((item) => item.id === id && item.status === 'running' ? { ...item, lines: [...item.lines, text] } : item));
    };
    const finish = (status: 'done' | 'failed' | 'cancelled', error?: string) => {
      cancels.current.delete(id);
      setProcesses((current) => current.map((item) => item.id === id && item.status === 'running' ? { ...item, status, error } : item));
      if (!group) setForegroundId((current) => current === id ? '' : current);
    };
    return {
      id,
      log,
      finish,
      bindCancel: (cancel) => { cancels.current.set(id, cancel); },
    };
  }, []);
  const cancelProcess = useCallback((id: string) => {
    cancels.current.get(id)?.();
    cancels.current.delete(id);
    setProcesses((current) => current.map((item) => item.id === id && item.status === 'running' ? { ...item, status: 'cancelled', lines: [...item.lines, 'Cancelled'] } : item));
    if (!groups.current.get(id)) setForegroundId((current) => current === id ? '' : current);
  }, []);
  const dismissProcess = useCallback((id: string) => {
    setProcesses((current) => current.filter((item) => item.id !== id || item.status === 'running'));
    setForegroundId((current) => current === id ? '' : current);
  }, []);
  const run = useCallback(async <T,>(label: string, work: (log: (line: string) => void, signal: AbortSignal) => Promise<T>, successText: string | ((value: T) => string), options?: RunOptions) => {
    if (options?.confirm !== false) {
      const explain = typeof options?.confirm === 'string' ? options.confirm : label;
      const ok = await confirm(label, explain);
      if (!ok) return undefined;
    }
    const controller = new AbortController();
    const proc = openProcess(label);
    proc.bindCancel(() => controller.abort());
    setForegroundId(proc.id);
    proc.log('Waiting for the server');
    try {
      const value = await work(proc.log, controller.signal);
      if (controller.signal.aborted) {
        proc.finish('cancelled');
        return undefined;
      }
      proc.finish('done');
      const text = typeof successText === 'function' ? successText(value) : successText;
      if (text) success(text);
      return value;
    } catch (err) {
      if (controller.signal.aborted || (err instanceof Error && err.message === 'Cancelled')) {
        proc.finish('cancelled');
        return undefined;
      }
      const message = apiError(err, 'That failed');
      proc.finish('failed', message);
      failure(message);
      return undefined;
    }
  }, [confirm, failure, openProcess, success]);
  const runGroup = useCallback(async <T,>(label: string, items: GroupItem<T>[], options?: RunOptions) => {
    if (options?.confirm !== false) {
      const explain = typeof options?.confirm === 'string' ? options.confirm : label;
      const ok = await confirm(label, explain);
      if (!ok) return undefined;
    }
    if (items.length <= 1) {
      const only = items[0];
      if (!only) return [];
      return [await run(only.title, only.work, '', { confirm: false })];
    }
    const group = `g${++seq.current}`;
    const handles = items.map((item) => {
      const controller = new AbortController();
      const proc = openProcess(item.title, group);
      proc.bindCancel(() => controller.abort());
      return { ...item, proc, controller };
    });
    setGroupLabel(label);
    setForegroundGroup(group);
    setForegroundId(handles[0]?.proc.id || '');
    return Promise.all(handles.map(async ({ work, proc, controller }) => {
      proc.log('Waiting for the server');
      try {
        const value = await work(proc.log, controller.signal);
        if (controller.signal.aborted) {
          proc.finish('cancelled');
          return undefined;
        }
        proc.finish('done');
        return value;
      } catch (err) {
        if (controller.signal.aborted || (err instanceof Error && err.message === 'Cancelled')) {
          proc.finish('cancelled');
          return undefined;
        }
        proc.finish('failed', apiError(err, 'That failed'));
        return undefined;
      }
    }));
  }, [confirm, openProcess, run]);
  const foreground = processes.find((item) => item.id === foregroundId && item.status === 'running');
  const groupMembers = foregroundGroup ? processes.filter((item) => item.group === foregroundGroup) : [];
  const groupLive = groupMembers.some((item) => item.status === 'running');
  const api = useMemo(() => ({ success, failure, confirm, openProcess, cancelProcess, dismissProcess, processes, run, runGroup, busy: Boolean(foreground) || groupLive }), [success, failure, confirm, openProcess, cancelProcess, dismissProcess, processes, run, runGroup, foreground, groupLive]);
  const fault = faults[0];
  return (
    <NoticeContext.Provider value={api}>
      {children}
      {groupLive ? (
        <GroupRunDialog
          label={groupLabel}
          items={groupMembers}
          selectedId={foregroundId}
          onSelect={setForegroundId}
          onCancel={cancelProcess}
          onBackground={() => { setForegroundGroup(''); setForegroundId(''); }}
        />
      ) : foreground ? (
        <div className="fixed inset-0 z-[70] flex items-end bg-gray-900/40 md:items-center md:justify-center md:p-4" onMouseDown={(event) => { event.stopPropagation(); if (event.target === event.currentTarget) setForegroundId(''); }}>
          <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white px-4 py-5 shadow-2xl dark:border-gray-700 dark:bg-gray-900 md:rounded-2xl md:px-6" role="status" aria-live="polite" aria-busy="true">
            <div className="flex items-center gap-4">
              <span className="bo-spinner" aria-hidden="true" />
              <p className="min-w-0 flex-1 text-base font-medium">{foreground.title}</p>
              <button type="button" aria-label="Close" className="shrink-0 rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => setForegroundId('')}><X className="h-4 w-4" /></button>
            </div>
            <div className="bo-terminal">
              {Array.from({ length: 10 }, (_, index) => {
                const start = Math.max(0, foreground.lines.length - 10);
                return <div key={index}>{foreground.lines[start + index] || ''}</div>;
              })}
            </div>
            <div className="mt-4 flex flex-col gap-2 md:flex-row md:justify-end">
              <Button variant="outline" onClick={() => cancelProcess(foreground.id)}>Cancel</Button>
              <Button onClick={() => setForegroundId('')}>Run in background</Button>
            </div>
          </div>
        </div>
      ) : null}
      {ask ? (
        <div className="fixed inset-0 z-[65] flex items-end bg-gray-900/40 md:items-center md:justify-center md:p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) answer(false); }}>
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900 md:rounded-2xl" role="alertdialog" aria-modal="true" aria-labelledby="notice-confirm-title">
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-4 dark:border-gray-700 md:px-6">
              <h3 id="notice-confirm-title" className="min-w-0 flex-1 truncate text-lg font-semibold">{ask.title}</h3>
              <button type="button" aria-label="Close" className="shrink-0 rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => answer(false)}><X className="h-4 w-4" /></button>
            </div>
            <p className="px-6 py-5 text-sm">{ask.explain}</p>
            <div className="flex flex-col gap-2 border-t border-gray-200 px-4 py-4 dark:border-gray-700 md:flex-row md:justify-end md:px-6">
              <Button variant="outline" onClick={() => answer(false)}>Cancel</Button>
              <Button onClick={() => answer(true)}>Proceed</Button>
            </div>
          </div>
        </div>
      ) : null}
      {fault ? (
        <div className="fixed inset-0 z-[80] flex items-end bg-gray-900/40 md:items-center md:justify-center md:p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) setFaults((current) => current.slice(1)); }}>
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900 md:rounded-2xl" role="alertdialog" aria-modal="true" aria-labelledby="notice-fault-title">
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-4 dark:border-gray-700 md:px-6">
              <h3 id="notice-fault-title" className="min-w-0 flex-1 truncate text-lg font-semibold">Something went wrong</h3>
              <button type="button" aria-label="Close" className="shrink-0 rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => setFaults((current) => current.slice(1))}><X className="h-4 w-4" /></button>
            </div>
            <p className="px-6 py-5 text-sm">{fault.text}</p>
            <div className="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-gray-700">
              <Button onClick={() => setFaults((current) => current.slice(1))}>Close</Button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2">
        {toasts.map((toast) => (
          <SuccessToast key={toast.id} text={toast.text} onDone={() => setToasts((current) => current.filter((item) => item.id !== toast.id))} />
        ))}
      </div>
    </NoticeContext.Provider>
  );
}
